import { validateViewPosition } from "src/utils";

const DESKTOP_BREAKPOINT = 960;

class SortDropdown extends HTMLElement {
  private trigger: HTMLElement | null = null;
  private popup: HTMLElement | null = null;
  private popupContent: HTMLElement | null = null;
  private template: HTMLTemplateElement | null = null;
  private events: (() => void)[] = [];
  private isOpen = false;

  connectedCallback(): void {
    this.trigger = this.querySelector("[data-sort-trigger]");
    this.popup = this.querySelector("[data-sort-popup]");
    this.popupContent = this.querySelector("[data-sort-popup-content]");
    this.template = this.querySelector("[data-sort-values]");

    this.init();
  }

  disconnectedCallback(): void {
    this.unload();
  }

  private isDesktop(): boolean {
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  }

  private init(): void {
    if (!this.trigger) return;

    const clickHandler = (e: Event) => {
      if (!this.isDesktop()) return;

      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    };
    this.trigger.addEventListener("click", clickHandler);
    this.events.push(() =>
      this.trigger?.removeEventListener("click", clickHandler),
    );

    const documentClickHandler = (e: Event) => {
      if (
        this.isOpen &&
        this.popup &&
        !this.popup.contains(e.target as Node) &&
        !this.trigger?.contains(e.target as Node)
      ) {
        this.close();
      }
    };
    document.addEventListener("click", documentClickHandler);
    this.events.push(() =>
      document.removeEventListener("click", documentClickHandler),
    );

    const keydownHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener("keydown", keydownHandler);
    this.events.push(() =>
      document.removeEventListener("keydown", keydownHandler),
    );

    const applyButton = this.querySelector("[data-sort-popup-apply]");
    if (applyButton) {
      const applyHandler = (e: Event) => {
        e.stopPropagation();
        this.applySort();
        this.close();
      };
      applyButton.addEventListener("click", applyHandler);
      this.events.push(() =>
        applyButton.removeEventListener("click", applyHandler),
      );
    }
  }

  private toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  private syncTemplateWithURL(): void {
    if (!this.template) return;

    const currentSort = new URLSearchParams(window.location.search).get(
      "sort_by",
    );
    const radios = this.template.content.querySelectorAll<HTMLInputElement>(
      ".sort-dropdown__radio",
    );

    radios.forEach((radio) => {
      radio.checked = currentSort
        ? radio.value === currentSort
        : radio.value === "manual";
    });
  }

  private open(): void {
    if (!this.popup || !this.template || !this.popupContent || !this.trigger)
      return;

    this.syncTemplateWithURL();

    this.popupContent.innerHTML = "";
    const content = this.template.content.cloneNode(true);
    this.popupContent.appendChild(content);

    this.isOpen = true;
    this.popup.classList.add("is-active");
    this.popup.setAttribute("aria-hidden", "false");
    this.trigger.classList.add("active");

    validateViewPosition(this.popup, this.trigger, this as HTMLElement);
  }

  private close(): void {
    if (!this.popup || !this.trigger) return;

    this.isOpen = false;
    this.popup.classList.remove("is-active");
    this.popup.setAttribute("aria-hidden", "true");
    this.trigger.classList.remove("active");
  }

  private applySort(): void {
    if (!this.popupContent) return;

    const selectedRadio = this.popupContent.querySelector<HTMLInputElement>(
      ".sort-dropdown__radio:checked",
    );
    if (!selectedRadio) return;

    const sortValue = selectedRadio.value;

    const filterDrawerForm =
      document.querySelector<HTMLFormElement>("[data-filter-form]");

    if (filterDrawerForm) {
      const safeSortValue =
        typeof CSS !== "undefined" && typeof CSS.escape === "function"
          ? CSS.escape(sortValue)
          : sortValue;
      const drawerRadio = filterDrawerForm.querySelector<HTMLInputElement>(
        `input[name="sort_by"][value="${safeSortValue}"]`,
      );
      if (drawerRadio) {
        drawerRadio.checked = true;
      }

      const applyButton =
        filterDrawerForm.querySelector<HTMLButtonElement>(
          '[data-button="apply"]',
        );
      if (applyButton) {
        applyButton.click();
      }
    } else {
      const params = new URLSearchParams(window.location.search);
      params.set("sort_by", sortValue);
      window.location.href = `${window.location.pathname}?${params.toString()}`;
    }

    if (this.template) {
      const templateRadios =
        this.template.content.querySelectorAll<HTMLInputElement>(
          ".sort-dropdown__radio",
        );
      templateRadios.forEach((radio) => {
        radio.checked = radio.value === sortValue;
      });
    }
  }

  private unload(): void {
    this.events.forEach((unsubscribe) => unsubscribe());
    this.close();
  }
}

if (!customElements.get("sort-dropdown")) {
  customElements.define("sort-dropdown", SortDropdown);
}

export default SortDropdown;
